/**
 * TopUpModal — THE top-up popup (2026-08-14 unification).
 *
 * Owner directive: every "pay for more PRO Work Units" entry point — the
 * wallet page buttons, the low-balance banners, chat, `?action=add-tokens`
 * deep links — opens THIS one modal, so the top-up experience is identical
 * everywhere. Design follows the wallet page's platform (gold) design
 * language; functionality is the superset of the two previous popups:
 *
 *   - by-USD / by-work-units entry modes (tabs)
 *   - suggested amounts (owner-directed ladder: $100 / $250 / $500 / $1,000)
 *   - saved cards with one-click selection + "use a new card" CardElement
 *   - live amount / transaction fee / total breakdown with pr0 estimate
 *   - in-place success (no navigation; realtime balance watcher)
 *   - auto top-up, ON BY DEFAULT for never-configured users but only
 *     ENROLLED when the user confirms by completing a top-up (never from
 *     merely opening the modal; a stored "off" choice is always respected;
 *     unchecking while enrolled disables immediately)
 *
 * Rendered once by TopUpModalProvider inside a Stripe <Elements> boundary,
 * portal-mounted to document.body.
 *
 * Purchase logic (money math UNCHANGED — display-only work here):
 *   stripeApi.paymentIntent({ operation: 'buy_tokens', tokens, amountUsd, baseFee, paymentMethodId })
 *   → stripe.confirmCardPayment(clientSecret, { payment_method })
 *   → stripeApi.confirmAndCreditPayment(paymentIntentId)
 * Fee is $0.30 + 2.9%; minimum top-up is $2. The charged-token derivation
 * uses the live rate; the pr0 numbers shown are display-only at the
 * published Standard Rate ($2.18 / pr0).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { APP_OVERLAY_Z_INDEX } from '../../styles/layers';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { stripeApi } from '../../services/stripeClient';
import { useMaybeWalletSummary } from '../../context/WalletSummaryContext';
import { useMaybeOLVRate } from '../../context/OLVRateContext';
import { useAutoTopUp } from '../../hooks/useAutoTopUp';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';
import {
  listOrgSharedPaymentMethods,
  SharedPaymentMethod,
} from '../../services/paymentMethodSharing';
import { AUTO_TOP_UP_DEFAULTS } from '../../types/autoTopUp';
import { useAccountBurnRate } from '../../hooks/useAccountBurnRate';
import { suggestTopUpAmounts } from '../../utils/burnRate';
import { pickDefaultChip, seedAutoTopUpFields } from '../../utils/topUpDecisions';
import { useTrialStatus } from '../../hooks/useTrialStatus';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import {
  usdToWu,
  wuToUsd,
  formatWu,
  WU_LABEL,
  STANDARD_RATE_USD_PER_WU,
} from '../../config/workUnits';
import { formatUsd } from '../common/CopyableAmount/utils';
import { redeemAccessCode } from '../../services/accessCodeClient';
import Pr0Unit from '../common/Pr0Unit';

// ============================================================================
// Constants
// ============================================================================

/**
 * Suggested amounts — owner-directed (2026-08-14): meaningful pr0 quantities,
 * much larger than the retired $10–$100 chips. USD mode defaults to $500
 * (matches the auto top-up default amount).
 */
const USD_PRESETS = [100, 250, 500, 1000];
const DEFAULT_USD_PRESET = 500;
/** Work-unit mode chips (≈ $109 / $218 / $545 / $1,090 at the Standard Rate). */
const WU_PRESETS = [50, 100, 250, 500];
const DEFAULT_WU_PRESET = 250;

const MIN_TOP_UP_USD = 2;
const FALLBACK_RATE_USD = 0.001;
/** If the realtime balance hasn't flipped this long after a confirmed credit, force success. */
const CREDIT_FALLBACK_MS = 12000;
/** Auto-dismiss the success state this long after it appears. */
const SUCCESS_AUTOCLOSE_MS = 2600;

/**
 * Idempotency attempt id (2026-08-18 audit): the server folds this into the
 * Stripe idempotency key, so a double-click or retried network call can
 * never charge twice. One id per ATTEMPT — kept across error retries,
 * re-minted when the amount/card changes or after a success.
 */
const newAttemptId = stripeApi.newPaymentAttemptId;

type SavedCard = { id: string; brand?: string; last4?: string; exp_month?: number; exp_year?: number; is_default?: boolean };
type Status = 'idle' | 'processing' | 'crediting' | 'success' | 'error';
type Mode = 'usd' | 'wu';

function feeFor(usd: number): number {
  return Math.round((0.3 + usd * 0.029) * 100) / 100;
}

// ============================================================================
// Animations + styled components — wallet-page (gold) design language
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Opened straight from the chat (GlobalChatSystem's low-balance path),
     so it must clear the pane in every mode — its old hand-picked number
     cleared the floating overlay but not the full-screen takeover. */
  z-index: ${APP_OVERLAY_Z_INDEX};
  animation: ${fadeIn} 0.18s ease;
  padding: 1rem;
  overflow-y: auto;

  @media (max-width: 480px) {
    align-items: flex-start;
    padding-top: calc(env(safe-area-inset-top, 0px) + 1rem);
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.75rem 2rem;
  width: 100%;
  /* Roomy enough that a full top-up (amount · payment · fee · auto top-up)
     fits without the fiddly incremental scrolling of the narrow card. */
  max-width: clamp(320px, 94vw, 760px);
  max-height: 92vh;
  overflow-y: auto;
  /* Nothing inside may push the card sideways — a horizontal scrollbar here
     clipped the right edge of the fee box and the auto top-up fields. */
  overflow-x: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  animation: ${slideUp} 0.22s ease;
  text-align: left;
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 12px;
    max-height: 94vh;
  }
`;

/**
 * Desktop body layout: what you're buying on the left, how you're paying on
 * the right. Halves the scroll height without hiding anything; stacks to the
 * original single column on narrow screens, where the order is unchanged.
 */
const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  column-gap: 1.6rem;

  @media (min-width: 720px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

/** Grid/flex children need an explicit zero min so long content can't blow out the row. */
const Col = styled.div`
  min-width: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
`;

const ModalTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0;
  text-transform: lowercase;
`;

const ModalSubtitle = styled.p`
  margin: 0.25rem 0 1.25rem 0;
  color: var(--brand-text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModeToggle = styled.div`
  display: flex;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 0.25rem;
  margin-bottom: 1.5rem;
`;

const ModeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: lowercase;
  background: ${(p) => (p.$active ? 'white' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-gold)' : 'var(--brand-text-secondary)')};
  box-shadow: ${(p) => (p.$active ? '0 2px 4px rgba(0,0,0,0.1)' : 'none')};

  &:hover {
    color: var(--brand-gold);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.15rem;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 600;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const PresetGrid = styled.div`
  display: grid;
  /* Two across at every width: each chip carries a dollar figure AND its pr0
     equivalent, which four-across squeezed into an overflow. */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const PresetButton = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0.6rem 0.25rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%)' : 'white')};
  border: 2px solid ${(p) => (p.$active ? 'var(--brand-gold-hover)' : 'var(--brand-line)')};

  &:hover {
    border-color: var(--brand-gold);
  }
`;

const PresetMain = styled.span<{ $active?: boolean }>`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--brand-text);
`;

const PresetSub = styled.span<{ $active?: boolean }>`
  font-size: 0.7rem;
  color: ${(p) => (p.$active ? '#3d2f10' : '#6b7280')};
  text-transform: lowercase;
`;

const InputWithAddon = styled.div`
  display: flex;
  align-items: stretch;
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: white;
  min-width: 0;
  max-width: 100%;

  &:focus-within {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }
`;

const InputAddon = styled.span<{ $position: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.875rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  border-right: ${(p) => (p.$position === 'left' ? '1px solid #e2e8f0' : 'none')};
  border-left: ${(p) => (p.$position === 'right' ? '1px solid #e2e8f0' : 'none')};
  user-select: none;
  min-width: ${(p) => (p.$position === 'right' ? '3.5rem' : 'auto')};
`;

const AdornedInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  font-size: 1rem;
  background: transparent;
  min-width: 0;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--brand-text-muted);
    text-transform: lowercase;
  }
`;

const GoldNote = styled.div`
  background: var(--brand-canvas);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin: 0 0 1.15rem;
  border-left: 4px solid var(--brand-gold);
`;

const GoldNoteText = styled.p`
  color: var(--brand-text-secondary);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
    color: var(--brand-gold);
    flex-shrink: 0;
  }

  strong {
    color: var(--brand-text);
  }
`;

const MethodRow = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: white;
  border: 2px solid ${(p) => (p.$active ? 'var(--brand-gold)' : 'var(--brand-line)')};
  box-shadow: ${(p) => (p.$active ? '0 0 0 3px rgba(198, 167, 94, 0.12)' : 'none')};
  cursor: pointer;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: var(--brand-text);
  transition: all 0.2s ease;
  text-transform: lowercase;

  &:hover {
    border-color: var(--brand-gold);
  }

  span[aria-hidden] {
    color: var(--brand-gold);
    font-weight: 700;
  }
`;

const CardElementBox = styled.div`
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  background: white;
  padding: 0.85rem 1rem;
  margin-top: 0.25rem;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }
`;

const FeeBox = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 0.5rem 0 1rem 0;
  border-left: 4px solid #9ca3af;
`;

const FeeRow = styled.div<{ $total?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #555;
  font-size: 0.9rem;
  text-transform: lowercase;
  margin: 0.15rem 0;
  ${(p) =>
    p.$total &&
    css`
      font-weight: 600;
      color: var(--brand-text);
    `}
`;

const FeeAmount = styled.span`
  color: var(--brand-text);
  font-weight: 600;
`;

const HelperText = styled.p`
  margin: 0.4rem 0 0 0;
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: lowercase;
`;

const ErrorText = styled.p`
  margin: 0.4rem 0 0 0;
  font-size: 0.8rem;
  color: #b91c1c;
  text-transform: lowercase;
`;

const AutoSection = styled.div`
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  padding: 1rem;
  margin: 0 0 1.15rem;
`;

const SwitchRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.9rem;
  color: var(--brand-text);
  cursor: pointer;
  text-transform: lowercase;

  input {
    accent-color: var(--brand-gold);
    margin-top: 2px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  strong {
    font-weight: 600;
  }
`;

const AutoFieldRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.85rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const AutoField = styled.div`
  flex: 1;
  min-width: 0;
`;

const AutoFieldLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.3rem;
  text-transform: lowercase;
`;

// ── Promo code (PR0-… wallet-credit codes) ──────────────────────────────────

const PromoRow = styled.div`
  margin: 0 0 1.15rem;
`;

const PromoToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  color: #8a7648;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  text-transform: lowercase;

  &:hover {
    color: var(--brand-text);
  }
`;

const PromoForm = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: stretch;
  margin-top: 0.6rem;
  flex-wrap: wrap;
`;

const PromoInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  background: white;

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  &::placeholder {
    color: #b3b3b3;
    letter-spacing: 0.04em;
  }
`;

const PromoApply = styled.button`
  padding: 0.7rem 1.2rem;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-transform: lowercase;
  background: white;
  color: var(--brand-text);
  border: 2px solid var(--brand-gold);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  ${(p) =>
    p.variant === 'primary'
      ? css`
          background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
          color: var(--brand-text);
          border: none;

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, var(--brand-gold-hover) 0%, #a07d3a 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(198, 167, 94, 0.3);
          }
        `
      : css`
          background: white;
          color: var(--brand-text-secondary);
          border: 2px solid var(--brand-line);

          &:hover:not(:disabled) {
            background: #f5f5f5;
            border-color: #ccc;
          }
        `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const TrialNote = styled.div<{ $warn?: boolean }>`
  background: ${(p) => (p.$warn ? '#FEF3C7' : '#EFF6FF')};
  border: 1px solid ${(p) => (p.$warn ? '#F59E0B' : '#BFDBFE')};
  color: ${(p) => (p.$warn ? '#78350F' : '#1E3A5F')};
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  font-size: 0.82rem;
  line-height: 1.55;
  margin-bottom: 1rem;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 0.75rem 0.5rem 0.25rem;
`;

const SuccessCircle = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #faf6ea 0%, #f0e6c8 100%);
  border: 2px solid var(--brand-gold);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
    color: var(--brand-gold-hover);
  }
`;

// ============================================================================
// Component
// ============================================================================

export interface TopUpModalProps {
  /** Optional free-text reason describing what triggered the modal (telemetry/UX). */
  reason?: string | null;
  /** Close the modal (provider clears isOpen). */
  onClose: () => void;
}

export const TopUpModal: React.FC<TopUpModalProps> = ({ reason, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const wallet = useMaybeWalletSummary();
  const olvRate = useMaybeOLVRate();
  const trialStatus = useTrialStatus();
  // Expired trial accounts cannot add tokens or credits — their Prest0
  // point of contact activates the full account instead.
  const trialBlocked = trialStatus.isTrialUser && trialStatus.isExpired;

  // PERSONAL balance on purpose (2026-07-13 account-wallet-display change):
  // the top-up credits the signed-in user's OWN wallet, and the crediting
  // success detector below watches this number rise — so it must track the
  // wallet the money actually lands in, never the (possibly account-scope)
  // displayed summary. Fallback keeps older provider shapes working in tests.
  //
  // Task #80 — ORG top-up: when the user is acting in a workspace whose
  // paying wallet is NOT their own (displayScope 'account'), the top-up
  // charges THEIR card but credits the ORG's wallet (billing.manage gated
  // below; the server enforces it again). In that mode the success detector
  // watches the DISPLAYED (account) balance — the org mirror is what rises.
  const usageVisibility = useUsageVisibility();
  const orgTopUpTarget =
    wallet?.displayScope === 'account' && wallet.displayAccountId ? wallet.displayAccountId : null;
  // `ready` gate (2026-08-18 audit): while the permission read is still in
  // flight canManageBilling is false, which flashed the blocked screen at
  // every org admin for a beat. Fail OPEN while loading (house contract —
  // the server re-gates every write anyway).
  const orgBlocked = orgTopUpTarget !== null && usageVisibility.ready && !usageVisibility.canManageBilling;
  const olvBalance = orgTopUpTarget
    ? wallet?.summary.olvBalance ?? 0
    : wallet?.personalSummary?.olvBalance ?? wallet?.summary.olvBalance ?? 0;
  const rate = olvRate?.getEffectiveRate() ?? FALLBACK_RATE_USD;

  // Firebase auth uid (Firestore autoTopUp rules key on the real auth uid).
  const [uid, setUid] = useState<string | null>(() => getAuth().currentUser?.uid ?? null);
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => setUid(u && !u.isAnonymous ? u.uid : null));
    return unsub;
  }, []);

  const autoTopUp = useAutoTopUp(uid);
  // This organization's own spending pace, for the personalised ladder below.
  const { burn } = useAccountBurnRate(uid);

  // Amount entry: by-USD / by-work-units modes, each with suggested chips.
  const [mode, setMode] = useState<Mode>('usd');
  const [selectedPreset, setSelectedPreset] = useState<number | 'custom'>(DEFAULT_USD_PRESET);
  const [customAmount, setCustomAmount] = useState('');

  const switchMode = useCallback((next: Mode) => {
    setMode(next);
    setSelectedPreset(next === 'usd' ? DEFAULT_USD_PRESET : DEFAULT_WU_PRESET);
    setCustomAmount('');
  }, []);

  /** The USD amount the user is buying (WU-mode entries convert at the Standard Rate). */
  const effectiveAmountUsd = useMemo(() => {
    const raw = selectedPreset === 'custom' ? parseFloat(customAmount) || 0 : selectedPreset;
    if (mode === 'wu') return Math.round(wuToUsd(raw) * 100) / 100;
    return raw;
  }, [mode, selectedPreset, customAmount]);

  // Payment method selection
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [selectedPmId, setSelectedPmId] = useState<string>('');
  const [useNewCard, setUseNewCard] = useState(false);
  // Task #80: cards other members SHARED with this organization — offered
  // only in org mode, only when the server grants the view permission (the
  // listing helper returns [] otherwise), and paying with one is re-gated
  // server-side on billing.payment-methods.use.
  const [orgSharedCards, setOrgSharedCards] = useState<SharedPaymentMethod[]>([]);
  const [selectedSharedPmId, setSelectedSharedPmId] = useState<string>('');

  // Purchase lifecycle
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Variant copy when success was forced by the crediting-fallback timer
  // (payment confirmed, credit still propagating).
  const [successNote, setSuccessNote] = useState<string | null>(null);
  // True only after a CARD purchase reaches 'crediting' — the auto top-up
  // enrollment effect requires it, so a promo-code redemption (which lands
  // in the same success state) can never enroll auto top-up.
  const cardPurchaseRef = useRef(false);

  // One idempotency attempt id per payment attempt (see newAttemptId).
  const attemptIdRef = useRef(newAttemptId());
  useEffect(() => {
    attemptIdRef.current = newAttemptId();
  }, [effectiveAmountUsd, selectedPmId, selectedSharedPmId, useNewCard]);
  useEffect(() => {
    if (status === 'success') attemptIdRef.current = newAttemptId();
  }, [status]);

  // Promo code (PR0-… wallet-credit codes). Redeeming is an alternative way
  // to add pr0 without a card, and lands in the SAME success state.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  /** pr0 credited by a redeemed code — drives the success copy. */
  const [codeCreditedWu, setCodeCreditedWu] = useState<number | null>(null);
  const baselineOlvRef = useRef<number | null>(null);
  const timersRef = useRef<number[]>([]);

  // Auto top-up UI. `autoWanted` is the checkbox: it defaults ON for users
  // who have never made an auto top-up choice, but enrollment only happens
  // when the user CONFIRMS by completing a top-up (see the success effect).
  // A stored choice (on OR off) always seeds the checkbox instead.
  const [autoWanted, setAutoWanted] = useState(false);
  const autoSeededRef = useRef(false);
  const [atuThreshold, setAtuThreshold] = useState(String(AUTO_TOP_UP_DEFAULTS.thresholdUsd));
  const [atuAmount, setAtuAmount] = useState(String(AUTO_TOP_UP_DEFAULTS.topUpAmountUsd));

  const registerTimer = useCallback((id: number) => {
    timersRef.current.push(id);
  }, []);

  useEffect(() => {
    const timers = timersRef;
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    };
  }, []);

  // Seed the auto top-up inputs. A STORED choice always wins — this must never
  // overwrite a figure the user set. Only a never-configured account gets the
  // usage-derived suggestion (~6 days of its own pace, with a threshold that
  // buys real runway) instead of the flat $25/$500 that charged Fernando.
  useEffect(() => {
    const seed = seedAutoTopUpFields({
      hasStoredSettings: autoTopUp.hasStoredSettings,
      stored: autoTopUp.settings,
      burn,
      fallback: AUTO_TOP_UP_DEFAULTS,
    });
    setAtuThreshold(String(seed.thresholdUsd));
    setAtuAmount(String(seed.topUpAmountUsd));
  }, [
    autoTopUp.hasStoredSettings,
    autoTopUp.settings.thresholdUsd,
    autoTopUp.settings.topUpAmountUsd,
    burn,
  ]);

  // Pre-select the "about a week" chip once the pace is known — the middle of
  // the ladder, not the biggest number on it. Never overrides a user's pick:
  // it runs once, and only while the untouched flat default is still selected.
  const presetSeededRef = useRef(false);
  useEffect(() => {
    if (presetSeededRef.current || !burn?.confident || mode !== 'usd') return;
    const ladder = suggestTopUpAmounts(burn);
    if (ladder.length === 0) return;
    presetSeededRef.current = true;
    if (selectedPreset !== DEFAULT_USD_PRESET) return; // the user already chose
    const chip = pickDefaultChip(ladder);
    if (chip) setSelectedPreset(chip.usd);
  }, [burn, mode, selectedPreset]);

  // Seed the checkbox from the user's OWN stored choice, and nothing else.
  //
  // INCIDENT 2026-09-10. This used to read
  //   settings.enabled || !hasStoredSettings
  // — pre-ticked for anyone who had never chosen. Combined with an enrolment
  // effect that fired on a success screen which auto-dismisses after 2.6s, a
  // customer 32 seconds past "work paused — wallet is empty" was signed up for
  // $500 recurring off-session charges without ever making a decision. Four
  // weeks later his card was charged and he found out from his bank.
  //
  // Owner ruling (Miles, 2026-09-11): opt-in, offered after the first real
  // payment — "nobody running on free credit is ever enrolled". The offer is
  // still shown here, because completing a card payment IS that first real
  // payment; it simply starts unticked, and the server refuses to arm an
  // account that has never knowingly paid (functions/src/billing/knowinglyPaid.ts).
  useEffect(() => {
    if (autoSeededRef.current || autoTopUp.isLoading) return;
    autoSeededRef.current = true;
    setAutoWanted(autoTopUp.settings.enabled);
  }, [autoTopUp.isLoading, autoTopUp.settings.enabled]);

  // Org mode: load the cards shared with this workspace (empty without the
  // view permission — the section simply doesn't render).
  useEffect(() => {
    let alive = true;
    if (!orgTopUpTarget) {
      setOrgSharedCards([]);
      setSelectedSharedPmId('');
      return;
    }
    void listOrgSharedPaymentMethods(orgTopUpTarget).then((methods) => {
      if (alive) setOrgSharedCards(methods);
    });
    return () => {
      alive = false;
    };
  }, [orgTopUpTarget]);

  // Fetch saved cards on mount to offer one-click pay.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingMethods(true);
      try {
        const res = await stripeApi.listMethods();
        const methods = ((res as { methods?: SavedCard[] })?.methods || []) as SavedCard[];
        if (!alive) return;
        setSavedCards(methods);
        if (methods.length > 0) {
          // Preselect the customer's DEFAULT card (2026-08-18 audit) — the
          // server marks it; first-in-list was an arbitrary choice.
          const preferred = methods.find((m) => m.is_default) ?? methods[0];
          setSelectedPmId(preferred.id);
          setUseNewCard(false);
        } else {
          setUseNewCard(true);
        }
      } catch {
        if (alive) setUseNewCard(true);
      } finally {
        if (alive) setLoadingMethods(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Escape-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && status !== 'processing') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [status, onClose]);

  // Lock body scroll while open — shared reference-counted lock (2026-08-12
  // scrolling round) so overlapping overlays can't restore each other's
  // snapshots out of order.
  useBodyScrollLock(true);

  // Success WITHOUT navigation: watch the realtime wallet balance rise above
  // the snapshot we took at submit time.
  useEffect(() => {
    if (status !== 'crediting') return;
    if (baselineOlvRef.current == null) return;
    if (olvBalance > baselineOlvRef.current + 1e-6) {
      setSuccessNote(null);
      setStatus('success');
    }
  }, [status, olvBalance]);

  // Crediting fallback: ALWAYS armed on entering 'crediting'. The payment is
  // confirmed at this point — if the realtime balance hasn't flipped us to
  // success in CREDIT_FALLBACK_MS, force success with honest variant copy
  // instead of holding the user hostage on a spinner.
  useEffect(() => {
    if (status !== 'crediting') return;
    const id = window.setTimeout(() => {
      setSuccessNote('payment received — your credit lands shortly.');
      setStatus('success');
    }, CREDIT_FALLBACK_MS);
    registerTimer(id);
    return () => window.clearTimeout(id);
  }, [status, registerTimer]);

  // Auto-dismiss the success state.
  useEffect(() => {
    if (status !== 'success') return;
    const id = window.setTimeout(() => onClose(), SUCCESS_AUTOCLOSE_MS);
    registerTimer(id);
    return () => window.clearTimeout(id);
  }, [status, onClose, registerTimer]);

  // Auto top-up ENROLLMENT: the completed top-up is the user's confirmation.
  // Runs once per modal lifetime, only on success, only when the box is
  // checked and auto top-up isn't already enrolled.
  const autoEnrolledRef = useRef(false);
  useEffect(() => {
    if (status !== 'success' || autoEnrolledRef.current) return;
    autoEnrolledRef.current = true;
    // Never enroll auto top-up off an ORG top-up — it's a personal
    // off-session flow and would silently refill the admin's own wallet.
    if (cardPurchaseRef.current && autoWanted && !autoTopUp.settings.enabled && uid && !orgTopUpTarget) {
      const approvedAmount = parseFloat(atuAmount) || AUTO_TOP_UP_DEFAULTS.topUpAmountUsd;
      autoTopUp
        .enable({
          thresholdUsd: parseFloat(atuThreshold) || AUTO_TOP_UP_DEFAULTS.thresholdUsd,
          topUpAmountUsd: approvedAmount,
          // The figure the user actually saw and agreed to. Suggested amounts
          // track usage, so the server may lower a future charge freely but
          // must never raise it past this without asking (autoTopUp.ts).
          confirmedAmountUsd: approvedAmount,
          paymentMethodId: !useNewCard && selectedPmId ? selectedPmId : undefined,
          // The digits are already on screen in the card picker. Recording them
          // here is what lets the pre-charge warning NAME the card, which is
          // what makes a charge recognisable rather than a surprise; the server
          // alternative is a Stripe round trip from a Firestore trigger.
          cardLast4:
            (!useNewCard && selectedPmId
              ? savedCards.find((c) => c.id === selectedPmId)?.last4
              : undefined) || undefined,
        })
        .catch(() => {
          /* surfaced via autoTopUp.error on next open; never blocks the success state */
        });
    }
  }, [status, autoWanted, autoTopUp, uid, atuThreshold, atuAmount, useNewCard, selectedPmId, orgTopUpTarget]);

  const fee = feeFor(effectiveAmountUsd);
  const total = Math.round((effectiveAmountUsd + fee) * 100) / 100;
  // Display-only WU estimate at the published Standard Rate ($2.18 / WU).
  // The payment flow below still uses the live rate — DO NOT reuse this there.
  const estWu = usdToWu(effectiveAmountUsd);
  const isBusy = status === 'processing' || status === 'crediting';
  const belowMin = effectiveAmountUsd < MIN_TOP_UP_USD;

  const handleSubmit = useCallback(async () => {
    setErrorMsg(null);
    const usd = effectiveAmountUsd;
    if (!usd || usd < MIN_TOP_UP_USD) {
      setErrorMsg(`minimum top-up is $${MIN_TOP_UP_USD.toFixed(2)}.`);
      return;
    }
    if (!stripe || !elements) {
      setErrorMsg('payments are still loading — please try again in a moment.');
      return;
    }
    if (!useNewCard && !selectedPmId && !selectedSharedPmId) {
      setErrorMsg('please choose a saved card or add a new one.');
      return;
    }

    const usdFee = feeFor(usd);
    const usdTotal = Math.round((usd + usdFee) * 100) / 100;
    const tokens = rate > 0 ? Math.max(1, Math.round(usd / rate)) : Math.max(1, Math.round(usd * 1000));

    baselineOlvRef.current = olvBalance;
    setSuccessNote(null);
    setStatus('processing');

    try {
      const usingSharedCard = !useNewCard && !!selectedSharedPmId && !!orgTopUpTarget;
      const { clientSecret, error } = await stripeApi.paymentIntent({
        operation: 'buy_tokens',
        tokens,
        amountUsd: usdTotal,
        baseFee: usdFee,
        paymentMethodId: useNewCard || usingSharedCard ? undefined : selectedPmId,
        // Org top-up: the server gates on billing.manage and stamps the
        // credit destination into the intent (the org's paying wallet).
        ...(orgTopUpTarget ? { accountId: orgTopUpTarget } : {}),
        // Shared org card: the server re-gates on the use permission and
        // creates the intent on the card owner's customer.
        ...(usingSharedCard ? { sharedPaymentMethodId: selectedSharedPmId } : {}),
        attemptId: attemptIdRef.current,
      });
      if (error || !clientSecret) {
        setStatus('error');
        setErrorMsg(error || 'could not start the payment. please try again.');
        return;
      }

      const confirmResult = useNewCard
        ? await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: elements.getElement(CardElement)! },
          })
        : await stripe.confirmCardPayment(clientSecret, {
            payment_method: usingSharedCard ? selectedSharedPmId : selectedPmId,
          });

      if (confirmResult.error) {
        setStatus('error');
        setErrorMsg(confirmResult.error.message || 'payment failed. please try again.');
        return;
      }

      // Move to "crediting": the WalletSummaryContext balance listener will
      // flip us to success once Firestore reflects the credit.
      cardPurchaseRef.current = true;
      setStatus('crediting');

      // The crediting-fallback timer is armed by the status-transition effect
      // below — ALWAYS, not just when this call succeeds (2026-08-18 audit:
      // a confirm throw or a {credited:false} result used to leave no timer,
      // stranding the modal on "crediting…" forever).
      const piId = confirmResult.paymentIntent?.id;
      if (piId) {
        try {
          await stripeApi.confirmAndCreditPayment(piId);
        } catch {
          /* webhook backup credits; the balance listener still resolves success */
        }
      }
    } catch (e) {
      setStatus('error');
      setErrorMsg((e as Error)?.message || 'something went wrong. please try again.');
    }
  }, [effectiveAmountUsd, stripe, elements, useNewCard, selectedPmId, selectedSharedPmId, rate, olvBalance, registerTimer, orgTopUpTarget]);

  /**
   * Redeem a promo code. The server credits the account's PAYING wallet and
   * returns the applied amount; we land in the same success state a card
   * purchase reaches, so the wallet balance the user is looking at updates
   * without a reload.
   */
  const handleRedeemPromo = useCallback(async () => {
    if (!promoCode.trim() || promoBusy) return;
    setPromoError(null);
    setPromoBusy(true);
    try {
      const result = await redeemAccessCode(promoCode);
      if (!result.success) {
        setPromoError(result.message);
        return;
      }
      const cents = typeof result.creditAmountCents === 'number' ? result.creditAmountCents : 0;
      // Display-derived pr0, same convention as every other surface:
      // displayed pr0 = credited USD ÷ the published Standard Rate.
      setCodeCreditedWu(cents > 0 ? usdToWu(cents / 100) : null);
      setPromoCode('');
      setStatus('success');
    } catch {
      setPromoError('we could not apply that code — please try again.');
    } finally {
      setPromoBusy(false);
    }
  }, [promoCode, promoBusy]);

  // Unchecking while ENROLLED turns auto top-up off immediately (turning it
  // off must never require a purchase). Checking is only ever a pending
  // intent confirmed by a completed top-up.
  const handleAutoToggle = useCallback(
    (next: boolean) => {
      setAutoWanted(next);
      if (!next && autoTopUp.settings.enabled) {
        autoTopUp.disable().catch(() => {
          /* surfaced via autoTopUp.error below */
        });
      }
    },
    [autoTopUp],
  );

  const persistAutoField = useCallback(
    (patch: { thresholdUsd?: number; topUpAmountUsd?: number }) => {
      if (!autoTopUp.settings.enabled) return; // only persist while enrolled
      // Editing the amount IS re-approving it — keep the ceiling in step, or
      // the server would hold every future charge at the older, lower figure.
      const withCeiling =
        typeof patch.topUpAmountUsd === 'number'
          ? { ...patch, confirmedAmountUsd: patch.topUpAmountUsd }
          : patch;
      autoTopUp.update(withCeiling).catch(() => {
        /* surfaced via autoTopUp.error */
      });
    },
    [autoTopUp],
  );

  // PERSONALISED LADDER (Miles, 2026-09-11). The chips are this organization's
  // own pace — roughly a few days / a week / two weeks / a month of it —
  // instead of a flat $100/$250/$500/$1,000 with $500 pre-selected. Each one
  // is labelled with how long it lasts, which is what makes the number mean
  // something before anyone clicks it.
  //
  // Falls back to the flat ladder, UNLABELLED, when there is too little
  // history: inventing a pace would be worse than showing round numbers.
  const suggestions = useMemo(
    () => (burn ? suggestTopUpAmounts(burn) : null),
    [burn],
  );
  const usdPresets = useMemo(
    () => (suggestions && burn?.confident ? suggestions.map((s) => s.usd) : USD_PRESETS),
    [suggestions, burn],
  );
  /** amount -> "about a week at your pace", when we know the pace. */
  const runwayFor = useMemo(() => {
    const map: Record<number, string> = {};
    if (suggestions && burn?.confident) for (const s of suggestions) map[s.usd] = s.subLabel;
    return map;
  }, [suggestions, burn]);

  const presets = mode === 'usd' ? usdPresets : WU_PRESETS;

  const content =
    status === 'success' ? (
      <SuccessWrap>
        <SuccessCircle>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </SuccessCircle>
        <ModalTitle as="h3">{codeCreditedWu != null ? 'code applied' : 'top-up complete'}</ModalTitle>
        <ModalSubtitle>
          {codeCreditedWu != null ? (
            <>
              {formatWu(codeCreditedWu)} <Pr0Unit /> added to your wallet. you can keep working
              right where you left off.
            </>
          ) : successNote ? (
            <>
              {successNote} you can keep working right where you left off.
              {autoWanted && autoTopUp.settings.enabled ? ' auto top-up is active.' : ''}
            </>
          ) : (
            <>
              your balance has been updated. you can keep working right where you left off.
              {autoWanted && autoTopUp.settings.enabled ? ' auto top-up is active.' : ''}
            </>
          )}
        </ModalSubtitle>
        <ModalActions style={{ justifyContent: 'center' }}>
          <ModalButton variant="primary" type="button" onClick={onClose}>
            done
          </ModalButton>
        </ModalActions>
      </SuccessWrap>
    ) : orgBlocked ? (
      <>
        <ModalHeader>
          <ModalTitle>this workspace&apos;s wallet</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalSubtitle>
          topping up this organization&apos;s wallet needs the billing permission —
          ask the owner for a top-up from the subscription page, or to grant you
          billing access.
        </ModalSubtitle>
        <ModalActions style={{ justifyContent: 'center' }}>
          <ModalButton variant="primary" type="button" onClick={onClose}>
            got it
          </ModalButton>
        </ModalActions>
      </>
    ) : trialBlocked ? (
      <>
        <ModalHeader>
          <ModalTitle>your trial has ended</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalSubtitle>
          trial accounts can't top up pr0 once the trial period is over. to keep
          your workspace, hire your Prest0n on the subscription page — everything stays.
        </ModalSubtitle>
        <ModalActions>
          <ModalButton variant="secondary" type="button" onClick={onClose}>
            cancel
          </ModalButton>
          <ModalButton
            variant="primary"
            type="button"
            onClick={() => {
              window.location.href = '/subscription';
            }}
          >
            activate my account
          </ModalButton>
        </ModalActions>
      </>
    ) : (
      <>
        <ModalHeader>
          <ModalTitle>
            top up <Pr0Unit />
          </ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close" disabled={status === 'processing'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalSubtitle>top up your wallet without leaving this page.</ModalSubtitle>

        {trialStatus.isTrialUser && trialStatus.isActive && (
          <TrialNote role="note" $warn={trialStatus.isExpiringSoon}>
            {trialStatus.isExpiringSoon
              ? `heads up — your trial ends in ${trialStatus.daysRemaining} day${trialStatus.daysRemaining === 1 ? '' : 's'}${trialStatus.endsOn ? ` (${trialStatus.endsOn})` : ''}. after that, trial accounts can't top up pr0 — your prest0 point of contact will activate your full account.`
              : `you're on a free trial${trialStatus.endsOn ? ` through ${trialStatus.endsOn}` : ''}. once it ends, trial accounts can't top up pr0 — your prest0 point of contact will activate your full account.`}
          </TrialNote>
        )}

        <ModeToggle role="tablist" aria-label="top-up entry mode">
          <ModeButton type="button" role="tab" aria-selected={mode === 'usd'} $active={mode === 'usd'} onClick={() => switchMode('usd')}>
            by usd amount
          </ModeButton>
          <ModeButton type="button" role="tab" aria-selected={mode === 'wu'} $active={mode === 'wu'} onClick={() => switchMode('wu')}>
            by work units
          </ModeButton>
        </ModeToggle>

        <BodyGrid>
        <Col>
        <FormGroup>
          <FormLabel>{mode === 'usd' ? 'amount to spend' : 'work units to top up'}</FormLabel>
          <PresetGrid>
            {presets.map((amt) => (
              <PresetButton
                key={amt}
                type="button"
                $active={selectedPreset === amt}
                onClick={() => setSelectedPreset(amt)}
              >
                {mode === 'usd' ? (
                  <>
                    <PresetMain>${amt.toLocaleString('en-US')}</PresetMain>
                    <PresetSub $active={selectedPreset === amt}>
                      {/* What the money BUYS beats what it converts to: a
                          person can consent to "about a week", not to 229 pr0. */}
                      {runwayFor[amt] ?? <>≈ {formatWu(usdToWu(amt))} {WU_LABEL}</>}
                    </PresetSub>
                  </>
                ) : (
                  <>
                    <PresetMain>
                      {amt.toLocaleString('en-US')} {WU_LABEL}
                    </PresetMain>
                    <PresetSub $active={selectedPreset === amt}>{formatUsd(wuToUsd(amt))}</PresetSub>
                  </>
                )}
              </PresetButton>
            ))}
          </PresetGrid>
          <InputWithAddon>
            {mode === 'usd' && <InputAddon $position="left">$</InputAddon>}
            <AdornedInput
              type="number"
              min={mode === 'usd' ? MIN_TOP_UP_USD : 1}
              step="1"
              inputMode="decimal"
              placeholder="custom amount"
              value={selectedPreset === 'custom' ? customAmount : ''}
              onChange={(e) => {
                setSelectedPreset('custom');
                setCustomAmount(e.target.value);
              }}
              onFocus={() => setSelectedPreset('custom')}
            />
            <InputAddon $position="right">{mode === 'usd' ? 'USD' : <Pr0Unit />}</InputAddon>
          </InputWithAddon>
          {effectiveAmountUsd > 0 && belowMin && (
            <HelperText>minimum top-up is ${MIN_TOP_UP_USD.toFixed(2)}.</HelperText>
          )}
        </FormGroup>

        <GoldNote>
          <GoldNoteText>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            {effectiveAmountUsd > 0 ? (
              <span>
                <strong>{formatUsd(effectiveAmountUsd)}</strong> ≈{' '}
                <strong>
                  {formatWu(estWu)} <Pr0Unit />
                </strong>{' '}
                (standard rate ${STANDARD_RATE_USD_PER_WU.toFixed(2)} / {WU_LABEL})
              </span>
            ) : (
              <span>
                standard rate ${STANDARD_RATE_USD_PER_WU.toFixed(2)} / {WU_LABEL}
              </span>
            )}
          </GoldNoteText>
        </GoldNote>
        </Col>

        <Col>
        <FormGroup>
          <FormLabel>payment method</FormLabel>
          {loadingMethods ? (
            <HelperText>loading saved cards…</HelperText>
          ) : (
            <>
              {orgSharedCards.length > 0 && (
                <>
                  <HelperText>shared with this organization</HelperText>
                  {orgSharedCards.map((c) => (
                    <MethodRow
                      key={c.id}
                      type="button"
                      $active={!useNewCard && selectedSharedPmId === c.id}
                      onClick={() => {
                        setUseNewCard(false);
                        setSelectedPmId('');
                        setSelectedSharedPmId(c.id);
                      }}
                    >
                      <span>
                        {(c.brand || 'card').toLowerCase()} •••• {c.last4 || '????'}
                        {c.mine ? ' — your card' : c.ownerLabel ? ` — shared by ${c.ownerLabel}` : ' — shared'}
                      </span>
                      {!useNewCard && selectedSharedPmId === c.id && <span aria-hidden>✓</span>}
                    </MethodRow>
                  ))}
                  {savedCards.length > 0 && <HelperText>your cards</HelperText>}
                </>
              )}
              {savedCards.map((c) => (
                <MethodRow
                  key={c.id}
                  type="button"
                  $active={!useNewCard && selectedPmId === c.id}
                  onClick={() => {
                    setUseNewCard(false);
                    setSelectedSharedPmId('');
                    setSelectedPmId(c.id);
                  }}
                >
                  <span>
                    {(c.brand || 'card').toLowerCase()} •••• {c.last4 || '????'}
                  </span>
                  {!useNewCard && selectedPmId === c.id && <span aria-hidden>✓</span>}
                </MethodRow>
              ))}
              <MethodRow type="button" $active={useNewCard} onClick={() => setUseNewCard(true)}>
                <span>use a new card</span>
                {useNewCard && <span aria-hidden>✓</span>}
              </MethodRow>
              {useNewCard && (
                <CardElementBox>
                  <CardElement options={{ hidePostalCode: true }} />
                </CardElementBox>
              )}
            </>
          )}
        </FormGroup>

        {effectiveAmountUsd >= MIN_TOP_UP_USD && (
          <FeeBox role="note" aria-live="polite">
            <FeeRow>
              <span>amount</span>
              <FeeAmount>{formatUsd(effectiveAmountUsd)}</FeeAmount>
            </FeeRow>
            <FeeRow>
              <span>transaction fee</span>
              <FeeAmount>{formatUsd(fee)}</FeeAmount>
            </FeeRow>
            <FeeRow $total>
              <span>total charged</span>
              <FeeAmount>{formatUsd(total)}</FeeAmount>
            </FeeRow>
            <HelperText>includes $0.30 + 2.9% of the transaction amount</HelperText>
            {orgTopUpTarget && (
              <HelperText>
                charges your card and credits this workspace&apos;s wallet
              </HelperText>
            )}
          </FeeBox>
        )}
        </Col>
        </BodyGrid>

        {!orgTopUpTarget && (
        <AutoSection>
          <SwitchRow>
            <input
              type="checkbox"
              checked={autoWanted}
              disabled={autoTopUp.isLoading || !uid || isBusy}
              onChange={(e) => handleAutoToggle(e.target.checked)}
            />
            <span>
              <strong>auto top-up</strong> — automatically top up when my balance runs low
            </span>
          </SwitchRow>
          <AutoFieldRow>
            <AutoField>
              <AutoFieldLabel>when below (usd)</AutoFieldLabel>
              <InputWithAddon>
                <InputAddon $position="left">$</InputAddon>
                <AdornedInput
                  type="number"
                  min={0}
                  step="1"
                  value={atuThreshold}
                  disabled={!autoWanted}
                  onChange={(e) => setAtuThreshold(e.target.value)}
                  onBlur={() => persistAutoField({ thresholdUsd: parseFloat(atuThreshold) || AUTO_TOP_UP_DEFAULTS.thresholdUsd })}
                />
              </InputWithAddon>
            </AutoField>
            <AutoField>
              <AutoFieldLabel>top up by (usd)</AutoFieldLabel>
              <InputWithAddon>
                <InputAddon $position="left">$</InputAddon>
                <AdornedInput
                  type="number"
                  min={MIN_TOP_UP_USD}
                  step="1"
                  value={atuAmount}
                  disabled={!autoWanted}
                  onChange={(e) => setAtuAmount(e.target.value)}
                  onBlur={() => persistAutoField({ topUpAmountUsd: parseFloat(atuAmount) || AUTO_TOP_UP_DEFAULTS.topUpAmountUsd })}
                />
              </InputWithAddon>
            </AutoField>
          </AutoFieldRow>
          <HelperText aria-live="polite">
            {autoTopUp.settings.enabled
              ? autoWanted
                ? `auto top-up is active — we'll top up $${parseFloat(atuAmount) || AUTO_TOP_UP_DEFAULTS.topUpAmountUsd} when your balance drops below $${parseFloat(atuThreshold) || AUTO_TOP_UP_DEFAULTS.thresholdUsd}, using your selected card.`
                : 'auto top-up has been turned off.'
              : autoWanted
                ? `activates when you confirm this top-up — we'll top up $${parseFloat(atuAmount) || AUTO_TOP_UP_DEFAULTS.topUpAmountUsd} when your balance drops below $${parseFloat(atuThreshold) || AUTO_TOP_UP_DEFAULTS.thresholdUsd}, using your selected card. uncheck to opt out.`
                : 'off — you can turn this on anytime.'}
          </HelperText>
          {autoTopUp.error && <ErrorText>{autoTopUp.error}</ErrorText>}
        </AutoSection>
        )}

        <GoldNote>
          <GoldNoteText>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <circle cx="12" cy="16" r="1" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            your payment is processed securely. work units will be added to your balance immediately after purchase.
          </GoldNoteText>
        </GoldNote>

        <PromoRow>
          {!promoOpen ? (
            <PromoToggle type="button" onClick={() => setPromoOpen(true)}>
              have a promo code?
            </PromoToggle>
          ) : (
            <>
              <FormLabel htmlFor="promo-code-input">promo code</FormLabel>
              <PromoForm>
                <PromoInput
                  id="promo-code-input"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    if (promoError) setPromoError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRedeemPromo();
                    }
                  }}
                  placeholder="PR0-XXXX-XXXX"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={promoBusy || isBusy}
                />
                <PromoApply
                  type="button"
                  onClick={handleRedeemPromo}
                  disabled={promoBusy || isBusy || !promoCode.trim()}
                >
                  {promoBusy ? 'applying…' : 'apply'}
                </PromoApply>
              </PromoForm>
              {promoError ? (
                <ErrorText role="alert">{promoError}</ErrorText>
              ) : (
                <HelperText>
                  credit lands in your wallet immediately — no card needed.
                </HelperText>
              )}
            </>
          )}
        </PromoRow>

        {errorMsg && <ErrorText role="alert">{errorMsg}</ErrorText>}

        <ModalActions>
          <ModalButton variant="secondary" type="button" onClick={onClose} disabled={status === 'processing'}>
            {status === 'crediting' ? 'close — credit lands shortly' : 'cancel'}
          </ModalButton>
          <ModalButton variant="primary" type="button" onClick={handleSubmit} disabled={isBusy || belowMin}>
            {status === 'processing' ? (
              'processing…'
            ) : status === 'crediting' ? (
              'confirming…'
            ) : (
              <>
                top up <Pr0Unit />
                {effectiveAmountUsd >= MIN_TOP_UP_USD ? ` — ${formatUsd(total)}` : ''}
              </>
            )}
          </ModalButton>
        </ModalActions>
      </>
    );

  const modal = (
    <Backdrop
      data-prest0n-portal="true"
      data-topup-reason={reason || undefined}
      role="dialog"
      aria-modal="true"
      aria-label="Top up pr0"
      onMouseDown={(e) => {
        // Backdrop click closes (but not while the CHARGE is in flight —
        // 'crediting' may close: payment done, the credit lands regardless).
        if (e.target === e.currentTarget && status !== 'processing') onClose();
      }}
    >
      <Card onMouseDown={(e) => e.stopPropagation()}>{content}</Card>
    </Backdrop>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default TopUpModal;
